<template>
  <form @submit.prevent="handleLogin" class="login-form">
    <h2 class="login-form__title">Sign In</h2>

    <div v-if="errorMessage" class="login-form__error" role="alert">
      {{ errorMessage }}
    </div>

    <div class="login-form__field">
      <label for="login-email" class="login-form__label">Email</label>
      <input
        id="login-email"
        v-model="email"
        type="email"
        class="login-form__input"
        placeholder="you@example.com"
        required
        :disabled="isLoading"
        autocomplete="email"
      />
    </div>

    <div class="login-form__field">
      <label for="login-password" class="login-form__label">Password</label>
      <input
        id="login-password"
        v-model="password"
        type="password"
        class="login-form__input"
        placeholder="Enter your password"
        required
        :disabled="isLoading"
        autocomplete="current-password"
      />
    </div>

    <button
      type="submit"
      class="login-form__button login-form__button--primary"
      :disabled="isLoading || !isFormValid"
    >
      {{ isLoading ? 'Signing in...' : 'Sign In' }}
    </button>

    <div class="login-form__divider">
      <span>or</span>
    </div>

    <button
      type="button"
      @click="handleGoogleLogin"
      class="login-form__button login-form__button--google"
      :disabled="isLoading"
    >
      <svg class="login-form__google-icon" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
      </svg>
      Continue with Google
    </button>

    <div class="login-form__footer">
      <router-link to="/forgot-password" class="login-form__link">
        Forgot password?
      </router-link>
      <router-link to="/signup" class="login-form__link">
        Create account
      </router-link>
    </div>
  </form>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useAuthStore } from '@/stores/auth';

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();

// Form state
const email = ref('');
const password = ref('');
const errorMessage = ref('');

// Loading state
const isLoading = computed(() => authStore.loading);

// Form validation
const isFormValid = computed(() => {
  return email.value.trim() !== '' && password.value.length >= 6;
});

const redirectTarget = computed(() => {
  const redirectQuery = route.query.redirect;
  if (typeof redirectQuery === 'string' && redirectQuery.startsWith('/')) {
    return redirectQuery;
  }
  return '/app';
});

async function handleLogin() {
  errorMessage.value = '';

  try {
    const { error } = await authStore.signIn(email.value, password.value);

    if (error) {
      errorMessage.value = error.message || 'Failed to sign in';
      return;
    }

    router.replace(redirectTarget.value);
  } catch (err) {
    console.error('Login error:', err);
    errorMessage.value = err.message || 'An unexpected error occurred';
  }
}

// Handle Google OAuth login
async function handleGoogleLogin() {
  errorMessage.value = '';

  try {
    const { error } = await authStore.signInWithGoogle(redirectTarget.value);

    if (error) {
      errorMessage.value = error.message || 'Failed to sign in with Google';
    }

    // Browser will redirect to Google - no need to handle success here
  } catch (err) {
    console.error('Google login error:', err);
    errorMessage.value = err.message || 'An unexpected error occurred';
  }
}
</script>

<style scoped>
.login-form {
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
  padding: var(--space-2xl);
  background: var(--color-bg-primary);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-lg);
}

.login-form__title {
  margin: 0 0 var(--space-xl) 0;
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
  text-align: center;
}

.login-form__error {
  margin-bottom: var(--space-lg);
  padding: var(--space-md);
  background: #fee;
  border: 1px solid var(--color-danger);
  border-radius: var(--radius-md);
  color: var(--color-danger);
  font-size: var(--font-size-sm);
}

.login-form__field {
  margin-bottom: var(--space-lg);
}

.login-form__label {
  display: block;
  margin-bottom: var(--space-sm);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-primary);
}

.login-form__input {
  width: 100%;
  padding: var(--space-md);
  font-size: var(--font-size-base);
  color: var(--color-text-primary);
  background: var(--color-bg-primary);
  border: 1px solid var(--color-border-medium);
  border-radius: var(--radius-md);
  transition: border-color var(--transition-fast);
}

.login-form__input:focus {
  outline: none;
  border-color: var(--color-accent);
}

.login-form__input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.login-form__input::placeholder {
  color: var(--color-text-tertiary);
}

.login-form__button {
  width: 100%;
  padding: var(--space-md) var(--space-lg);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all var(--transition-fast);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-sm);
}

.login-form__button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.login-form__button--primary {
  background: var(--color-accent);
  color: var(--color-burgundy-dark);
}

.login-form__button--primary:hover:not(:disabled) {
  background: var(--color-accent-hover);
}

.login-form__button--google {
  margin-top: var(--space-md);
  background: var(--color-bg-primary);
  color: var(--color-text-primary);
  border: 1px solid var(--color-border-medium);
}

.login-form__button--google:hover:not(:disabled) {
  background: var(--color-bg-secondary);
}

.login-form__google-icon {
  width: 20px;
  height: 20px;
}

.login-form__divider {
  margin: var(--space-xl) 0;
  text-align: center;
  position: relative;
}

.login-form__divider::before,
.login-form__divider::after {
  content: '';
  position: absolute;
  top: 50%;
  width: calc(50% - var(--space-2xl));
  height: 1px;
  background: var(--color-border-light);
}

.login-form__divider::before {
  left: 0;
}

.login-form__divider::after {
  right: 0;
}

.login-form__divider span {
  padding: 0 var(--space-md);
  font-size: var(--font-size-sm);
  color: var(--color-text-tertiary);
  background: var(--color-bg-primary);
}

.login-form__footer {
  margin-top: var(--space-xl);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.login-form__link {
  font-size: var(--font-size-sm);
  color: var(--color-accent);
  text-decoration: none;
  transition: color var(--transition-fast);
}

.login-form__link:hover {
  color: var(--color-accent-hover);
  text-decoration: underline;
}

.login-form__link:focus-visible {
  outline: 2px solid var(--color-accent);
  outline-offset: 2px;
  border-radius: var(--radius-sm);
}
</style>

